/**
 * Created by neo on 02.03.23.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { ExploreEntry } from '../../../Model/Explore/ExploreEntry';
import { ExploreEntryBuilder } from '../../../Model/Explore/ExploreEntryBuilder';
import { Container } from 'reactstrap';
import { Theme } from '../../../Theme/Theme';
import { useTrans } from '../../../Store/System/LocalizationStore';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Translation } from '../../../Components/Translation';
import { ExploreFilterTagsSection } from '../../Academy/ExploreFilterTagsSection';
import { MagazineCard } from '../../../Components/ExploreCards/MagazineCard';
import { subtreeCategoryIdsOfAsync } from '../../../Model/Explore/exploreCategoryTreeV3';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const ScreenContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  padding-bottom: 48px;
`;

const HeroSection = styled.div`
  background: white;
  padding: 32px 0 24px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const AccentText = styled.span`
  background: linear-gradient(135deg, ${Theme.Colors.primary}, ${Theme.Colors.primaryDark});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const HeroSubtitle = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
  max-width: 600px;

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

const FiltersTagsContainer = styled.div`
  padding: 24px 0 32px 0;
`;

const EntriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const LoadMoreSentinel = styled.div`
  height: 1px;
  margin-top: 32px;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  gap: 12px;
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid ${Theme.Colors.lighterGrey};
  border-top-color: ${Theme.Colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const LoadingText = styled.span`
  font-size: 0.875rem;
  color: ${Theme.Colors.charcoal};
`;

const MOVEMENT_CATEGORY = 'movement';
const PAGE_SIZE = 16;
const EMPTY_IDS: string[] = [];
const ENTRY_TYPES = ['video', 'article', 'activityWorkout', 'yoga', 'workoutTemplate'];

export type MoveOndemandLibraryScreenProps = {};

export const MoveOndemandLibraryScreen: React.FC<MoveOndemandLibraryScreenProps> = observer((props) => {
  const { t } = useTrans();

  const [loading, setLoading] = useState(true);
  const [subtreeIds, setSubtreeIds] = useState<string[] | undefined>(undefined);
  const [entries, setEntries] = useState<ExploreEntry[]>([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  // The V3 entries endpoint only matches DIRECT children of a parentId, and
  // 'movement' is a slug — resolve it and collect the whole subtree once.
  useEffect(() => {
    subtreeCategoryIdsOfAsync(MOVEMENT_CATEGORY)
      .then((ids) => setSubtreeIds(ids))
      .catch(() => setSubtreeIds([]));
  }, []);

  useEffect(() => {
    if (!subtreeIds) return;
    if (subtreeIds.length === 0) {
      setEntries([]);
      setLoading(false);
      return;
    }
    loadingRef.current = true;
    setLoading(true);
    Promise.all(
      subtreeIds.map((id) =>
        ExploreEntryBuilder.find({
          rootCategories: [id],
          filterTags: selectedTags,
          type: ENTRY_TYPES,
          size: 100,
          sort: 'createdAt,DESC',
        }),
      ),
    )
      .then((results) => {
        const seen = new Set<string>();
        const merged = results
          .flat()
          .filter((entry) => (seen.has(entry.id) ? false : (seen.add(entry.id), true)))
          .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
        setEntries(merged);
        setVisibleCount(PAGE_SIZE);
      })
      .catch(() => setEntries([]))
      .finally(() => {
        setLoading(false);
        loadingRef.current = false;
      });
  }, [subtreeIds, selectedTags]);

  const hasMore = visibleCount < entries.length;

  useEffect(() => {
    if (!hasMore) return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (obsEntries) => {
        if (obsEntries[0].isIntersecting && !loadingRef.current) {
          setVisibleCount((c) => c + PAGE_SIZE);
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore]);

  const handleSelectFilterTag = useCallback((tags: string[]) => {
    setVisibleCount(PAGE_SIZE);
    setSelectedTags(tags);
  }, []);

  return (
    <ScreenContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <Translation i18nKey="moveLibrary.title" defaultValue="On Demand Library" />
          </HeroTitle>
          <HeroSubtitle>
            <Translation
              i18nKey="moveLibrary.description"
              defaultValue="Missed a live training? No worries. You can find a selection of old live workouts in our on demand library."
            />
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>
      <Container>
        <FiltersTagsContainer>
          <ExploreFilterTagsSection
            categories={subtreeIds ?? EMPTY_IDS}
            selectedTags={selectedTags}
            onFilter={handleSelectFilterTag}
          />
        </FiltersTagsContainer>
        <EntriesGrid>
          {entries.slice(0, visibleCount).map((entry) => (
            <MagazineCard key={entry.id} entry={entry} linkPrefix="/move/" />
          ))}
        </EntriesGrid>
        {loading && (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>{t('globals.loading', { defaultValue: 'Loading...' })}</LoadingText>
          </LoadingContainer>
        )}
        {!loading && hasMore && <LoadMoreSentinel ref={sentinelRef} />}
      </Container>
    </ScreenContainer>
  );
});
